import type {
  AppState,
  Auth0ContextInterface,
  LogoutOptions,
  RedirectLoginOptions,
  User,
} from "@auth0/auth0-react";
import { useAuth0 as webUseAuth0 } from "@auth0/auth0-react";
import { Browser } from "@capacitor/browser";
import React from "react";
import { env } from "../env";
import { captureException } from "../sentry";
import { browserState } from "./browser-state";
import { canUseNative, getNativeAppRedirectUri } from "./utils";

// Mutex to prevent concurrent Browser.open() calls.
// On iOS, calling Browser.open() while another is in progress causes
// "Unable to display URL" errors and triggers an infinite loop.
let browserOpenLock: Promise<void> = Promise.resolve();
const RECENT_AUTH0_LOGOUT_WINDOW_MS = 20000;
let auth0LogoutInFlight = false;
let lastAuth0LogoutAt = 0;

const markAuth0LogoutStarted = () => {
  auth0LogoutInFlight = true;
  lastAuth0LogoutAt = Date.now();
};

const markAuth0LogoutFinished = () => {
  auth0LogoutInFlight = false;
};

const hasRecentAuth0Logout = (
  windowMs: number = RECENT_AUTH0_LOGOUT_WINDOW_MS
) => {
  return Date.now() - lastAuth0LogoutAt < windowMs;
};

const isAuth0LogoutInFlight = () => auth0LogoutInFlight;

// Be careful when tweeking this code, there is many things who rely on it on mobile side
// If you do any changes make sure to test the following path in both Android AND iOS
// - Uninstall app,, install and login from scratch
// - Logout
// - Put the app in background, then go back in it, multiples times
// - Clean the phone processes, then go back in the app
// - Reboot the phone, then go back in the app
// - Close the browser window of the "login page" instead of login in
// - Login
// - Put the app in background, then go back in it, multiples times
// - Clean the phone processes, then go back in the app
// - Reboot the phone, then go back in the app
// All of those cases manage memory and resuming in a slightly different manner
// so you want to make sure you don't have weird behaviors in any of those scenarios
function useNativeUseAuth0() {
  const defaultUseAuth0 = webUseAuth0();
  const { loginWithRedirect: auth0LoginWithRedirect, logout: auth0Logout } =
    defaultUseAuth0;

  const openUrl = async (
    url: string,
    options: { throwOnFailure?: boolean } = {}
  ) => {
    const customUrl = new URL(url);
    // On mobile, we add the current app https base url to allow auth0 action to know which url
    // to hit for his webhooks
    customUrl.searchParams.append("webhook_origin", env.VITE_APP_BASE_URL);
    const finalUrl = customUrl.toString();

    // Serialize Browser.open() calls: wait for any previous call to finish
    // before starting a new one.  On iOS, concurrent opens cause
    // "Unable to display URL" errors and trigger an infinite loop.
    const previous = browserOpenLock;
    let releaseLock!: () => void;
    browserOpenLock = new Promise<void>((resolve) => {
      releaseLock = resolve;
    });

    try {
      await previous;
    } catch {
      // ignore errors from previous call
    }

    const attemptOpen = async (): Promise<void> => {
      // Close any lingering browser before opening a new one
      await Browser.close().catch(() => undefined);
      browserState.setOpen(true);
      await Browser.open({
        url: finalUrl,
        windowName: "_self",
      });
    };

    try {
      await attemptOpen();
    } catch (firstError) {
      browserState.setOpen(false);
      captureException(firstError, {
        extra: { action: "openUrl", attempt: 1, url: finalUrl },
      });

      // Wait briefly and retry once — the Browser plugin may need time to
      // stabilise after a webview reload (LiveUpdate).
      await new Promise((resolve) => setTimeout(resolve, 1000));

      try {
        await Browser.close().catch(() => undefined);
        await attemptOpen();
      } catch (retryError) {
        browserState.setOpen(false);
        captureException(retryError, {
          extra: { action: "openUrl", attempt: 2, url: finalUrl },
        });
        if (options.throwOnFailure) {
          throw retryError;
        }
      }
    } finally {
      releaseLock();
    }
  };

  const loginWithRedirect = React.useCallback(
    async (
      options: RedirectLoginOptions<AppState> = {
        appState: {
          returnTo: getNativeAppRedirectUri(),
        },
      }
    ) => {
      await auth0LoginWithRedirect({
        ...options,
        openUrl: (url) => openUrl(url, { throwOnFailure: true }),
      });
    },
    [auth0LoginWithRedirect]
  );

  const logout = React.useCallback(
    async (
      options: LogoutOptions = {
        logoutParams: {
          returnTo: getNativeAppRedirectUri(),
        },
      }
    ) => {
      markAuth0LogoutStarted();
      try {
        await auth0Logout({
          ...options,
          openUrl,
        });
      } finally {
        markAuth0LogoutFinished();
      }
    },
    [auth0Logout]
  );

  return {
    ...defaultUseAuth0,
    loginWithRedirect,
    logout,
  };
}

const useAuth0 = <
  TUser extends User = User
>(): Auth0ContextInterface<TUser> => {
  if (canUseNative() === false) {
    return webUseAuth0();
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useNativeUseAuth0() as Auth0ContextInterface<TUser>;
};

type UserPropsAuth0 = {
  impersonation?: {
    "impersonator-email": string;
    /* The impersonator Clovis id */
    "impersonator-id": string;
    "usurped-user-email": string;
    /* The usurped user Auth0 id */
    "usurped-user-sub": string;
  };
  /* Account linking exist when the user is connected with microsoft sso and have an existing account auth0 */
  account_linking?: {
    "primary-account-email": string;
    /* The primary account Auth0 id (connection Auth0) */
    "primary-account-sub": string;
  };
  "https://hasura.io/jwt/claims": {
    "x-hasura-allowed-roles": string[];
    "x-hasura-default-role": string;
    "x-hasura-user-email": string;
    "x-hasura-user-id": string;
  };
  iss: string;
  /* the sub is the user identifier, meaning "subject", often in the structure of "auth0|12345678..." with is the Auth0 id */
  sub: string;
  aud: string[];
  iat: number;
  exp: number;
  scope: string;
  azp: string;
};

function getAuth0Id(user: UserPropsAuth0 | undefined) {
  return (
    user?.impersonation?.["usurped-user-sub"] ??
    user?.account_linking?.["primary-account-sub"] ??
    user?.sub
  );
}

export {
  canUseNative,
  getAuth0Id,
  getNativeAppRedirectUri,
  hasRecentAuth0Logout,
  isAuth0LogoutInFlight,
  useAuth0,
};
export type { UserPropsAuth0 };
